import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {Form, Table} from "react-bootstrap";
import {useNavigate, useParams} from "react-router";
import AdminApi from "common/api";
import Tabs from "common/components/Tabs";
import Pagination from "common/components/Pagination";
import ModalDialog from "common/components/Modal";
import {AlertContext} from "context/AlertContext";
import {dateFormat} from "common/utils";

const PAGE_SIZE = 30;

function UserInfoBar({user}) {
  if (!user) return null;

  const items = [
    {label: "회원번호", value: user.userNo},
    {label: "닉네임", value: user.nickname || "-"},
    {label: "로그인", value: user.loginType || "-"},
    {label: "포인트", value: (user.point ?? 0).toLocaleString()},
    {label: "간식", value: user.snackCount ?? 0},
    {label: "상태", value: user.status || "-"},
    {label: "가입일", value: dateFormat(user.createdAt, true)},
  ];

  return (
    <div className="user-info-bar">
      {items.map((item) => (
        <div key={item.label} className="user-info-item">
          <span className="user-info-label">{item.label}</span>
          <span className="user-info-value">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

function PointHistoryTab({userNo, onGranted}) {
  const api = new AdminApi();
  const {setShow, setToastMsg} = useContext(AlertContext);
  const currentPage = useRef(1);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [grantOpen, setGrantOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const parsedAmount = Number(amount);

  const fetchHistory = useCallback((page = 1) => {
    api.getAdminUserPointHistory(userNo, {page, countPerPage: PAGE_SIZE})
      .then((res) => {
        if (res?.result === "ok") {
          setItems(res.infos ?? []);
          setTotalCount(res.pageInfo?.totalItemCount ?? 0);
          currentPage.current = page;
        } else {
          setItems([]);
          setTotalCount(0);
        }
      });
  }, [userNo]);

  useEffect(() => {
    fetchHistory(1);
  }, [fetchHistory]);

  function openGrantModal() {
    setAmount("");
    setReason("");
    setGrantOpen(true);
  }

  function onSubmitGrant() {
    if (!Number.isInteger(parsedAmount) || parsedAmount === 0) {
      setToastMsg("0이 아닌 정수 포인트를 입력해주세요.");
      setShow(true);
      return;
    }
    setConfirmOpen(true);
  }

  function onConfirmGrant() {
    if (submitting) return;
    setSubmitting(true);
    api.adminGrantPoint(userNo, {
      amount: parsedAmount,
      reason: reason.trim() || undefined,
    }).then((res) => {
      if (res?.result === "ok") {
        setToastMsg(parsedAmount > 0
          ? `${parsedAmount.toLocaleString()}P가 지급되었습니다.`
          : `${Math.abs(parsedAmount).toLocaleString()}P가 차감되었습니다.`);
        setShow(true);
        setConfirmOpen(false);
        setGrantOpen(false);
        fetchHistory(1);
        onGranted?.();
      } else {
        setToastMsg(res?.message || "포인트 처리에 실패했습니다.");
        setShow(true);
        setConfirmOpen(false);
      }
    }).finally(() => setSubmitting(false));
  }

  return (
    <div>
      <div className="user-coupon-toolbar flex">
        <button type="button" className="blue-btn" onClick={openGrantModal}>
          포인트 지급
        </button>
      </div>

      <Table bordered className="basic-table user-history-table">
        <thead>
          <tr>
            <th>일시</th>
            <th>변동</th>
            <th>내용</th>
            <th>유형</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? items.map((item, idx) => (
            <tr key={`${item.createdAt}-${idx}`}>
              <td>{dateFormat(item.createdAt, true)}</td>
              <td className={item.amount > 0 ? "text-plus" : "text-minus"}>
                {item.amount > 0 ? "+" : ""}{item.amount?.toLocaleString()}
              </td>
              <td>{item.description || "-"}</td>
              <td>{item.type || "-"}</td>
            </tr>
          )) : (
            <tr>
              <td colSpan={4} style={{textAlign: "center"}}>포인트 내역이 없습니다.</td>
            </tr>
          )}
        </tbody>
      </Table>
      {totalCount > 0 &&
        <Pagination
          total={totalCount}
          pageSize={PAGE_SIZE}
          page={currentPage.current}
          onPageChange={fetchHistory}
        />
      }

      <ModalDialog
        show={grantOpen}
        title="포인트 지급/차감"
        onHide={() => setGrantOpen(false)}
        btnText="적용하기"
        hasTwoButtons
        secondBtnText="취소"
        onOk={onSubmitGrant}
        onClickSecond={() => setGrantOpen(false)}
      >
        <div className="user-grant-form">
          <p className="user-grant-note">양수는 지급, 음수는 차감입니다. point_history와 admin_grant_log에 함께 기록됩니다.</p>
          <label>포인트</label>
          <Form.Control
            type="number"
            placeholder="예: 1000 또는 -500"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <label>사유</label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="예: 이벤트 보상, CS 보상 등"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </ModalDialog>

      <ModalDialog
        show={confirmOpen}
        title="확인"
        onHide={() => !submitting && setConfirmOpen(false)}
        btnText={submitting ? "처리 중..." : "확인"}
        hasTwoButtons
        secondBtnText="취소"
        onOk={onConfirmGrant}
        onClickSecond={() => !submitting && setConfirmOpen(false)}
      >
        <p style={{textAlign: "center"}}>
          {Number.isInteger(parsedAmount) && parsedAmount !== 0
            ? `${Math.abs(parsedAmount).toLocaleString()}P를 ${parsedAmount > 0 ? "지급" : "차감"}하시겠습니까?`
            : "포인트를 적용하시겠습니까?"}
        </p>
      </ModalDialog>
    </div>
  );
}

function SnackHistoryTab({userNo, onGranted}) {
  const api = new AdminApi();
  const {setShow, setToastMsg} = useContext(AlertContext);
  const currentPage = useRef(1);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [grantOpen, setGrantOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const parsedAmount = Number(amount);

  const fetchHistory = useCallback((page = 1) => {
    api.getAdminUserSnackHistory(userNo, {page, rowPerPage: PAGE_SIZE})
      .then((res) => {
        if (res?.result === "ok") {
          setItems(res.list ?? []);
          setTotalCount(res.pageInfo?.totalItemCount ?? 0);
          currentPage.current = page;
        } else {
          setItems([]);
          setTotalCount(0);
        }
      });
  }, [userNo]);

  useEffect(() => {
    fetchHistory(1);
  }, [fetchHistory]);

  function openGrantModal() {
    setAmount("");
    setReason("");
    setGrantOpen(true);
  }

  function onSubmitGrant() {
    if (!Number.isInteger(parsedAmount) || parsedAmount === 0) {
      setToastMsg("0이 아닌 정수 간식 수량을 입력해주세요.");
      setShow(true);
      return;
    }
    setConfirmOpen(true);
  }

  function onConfirmGrant() {
    if (submitting) return;
    setSubmitting(true);
    api.adminGrantSnack(userNo, {
      amount: parsedAmount,
      reason: reason.trim() || undefined,
    }).then((res) => {
      if (res?.result === "ok") {
        setToastMsg(parsedAmount > 0
          ? `간식 ${parsedAmount.toLocaleString()}개가 지급되었습니다.`
          : `간식 ${Math.abs(parsedAmount).toLocaleString()}개가 차감되었습니다.`);
        setShow(true);
        setConfirmOpen(false);
        setGrantOpen(false);
        fetchHistory(1);
        onGranted?.();
      } else {
        setToastMsg(res?.message || "간식 처리에 실패했습니다.");
        setShow(true);
        setConfirmOpen(false);
      }
    }).finally(() => setSubmitting(false));
  }

  return (
    <div>
      <div className="user-coupon-toolbar flex">
        <button type="button" className="blue-btn" onClick={openGrantModal}>
          간식 지급
        </button>
      </div>

      <Table bordered className="basic-table user-history-table">
        <thead>
          <tr>
            <th>일시</th>
            <th>변동</th>
            <th>구분</th>
            <th>항목</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? items.map((item) => (
            <tr key={item.idx}>
              <td>{dateFormat(item.createdAt, true)}</td>
              <td className={item.delta > 0 ? "text-plus" : "text-minus"}>
                {item.delta > 0 ? "+" : ""}{item.delta}
              </td>
              <td>{item.itemTitle || "-"}</td>
              <td>{item.itemName || "-"}</td>
            </tr>
          )) : (
            <tr>
              <td colSpan={4} style={{textAlign: "center"}}>간식 내역이 없습니다.</td>
            </tr>
          )}
        </tbody>
      </Table>
      {totalCount > 0 &&
        <Pagination
          total={totalCount}
          pageSize={PAGE_SIZE}
          page={currentPage.current}
          onPageChange={fetchHistory}
        />
      }

      <ModalDialog
        show={grantOpen}
        title="간식 지급/차감"
        onHide={() => setGrantOpen(false)}
        btnText="적용하기"
        hasTwoButtons
        secondBtnText="취소"
        onOk={onSubmitGrant}
        onClickSecond={() => setGrantOpen(false)}
      >
        <div className="user-grant-form">
          <p className="user-grant-note">양수는 지급, 음수는 차감입니다. user_snack_history와 admin_grant_log에 함께 기록됩니다.</p>
          <label>간식 수량</label>
          <Form.Control
            type="number"
            placeholder="예: 10 또는 -5"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <label>사유</label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="예: 이벤트 보상, CS 보상 등"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </ModalDialog>

      <ModalDialog
        show={confirmOpen}
        title="확인"
        onHide={() => !submitting && setConfirmOpen(false)}
        btnText={submitting ? "처리 중..." : "확인"}
        hasTwoButtons
        secondBtnText="취소"
        onOk={onConfirmGrant}
        onClickSecond={() => !submitting && setConfirmOpen(false)}
      >
        <p style={{textAlign: "center"}}>
          {Number.isInteger(parsedAmount) && parsedAmount !== 0
            ? `간식 ${Math.abs(parsedAmount).toLocaleString()}개를 ${parsedAmount > 0 ? "지급" : "차감"}하시겠습니까?`
            : "간식을 적용하시겠습니까?"}
        </p>
      </ModalDialog>
    </div>
  );
}

const COUPON_STATUS_LABEL = {
  AVAILABLE: "사용가능",
  USED: "사용완료",
  EXPIRED: "만료",
};

function CouponHistoryTab({userNo}) {
  const api = new AdminApi();
  const {setShow, setToastMsg} = useContext(AlertContext);
  const [items, setItems] = useState([]);

  const [grantOpen, setGrantOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [brands, setBrands] = useState([]);
  const [goods, setGoods] = useState([]);
  const [brandName, setBrandName] = useState("전체");
  const [goodsId, setGoodsId] = useState("");
  const [reason, setReason] = useState("");

  const selectedGoods = goods.find((g) => String(g.goodsId) === String(goodsId));

  const fetchCoupons = useCallback(() => {
    api.getAdminUserCouponHistory(userNo).then((res) => {
      if (res?.result === "ok") {
        setItems(res.infos ?? []);
      } else {
        setItems([]);
      }
    });
  }, [userNo]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  function openGrantModal() {
    setBrandName("전체");
    setGoodsId("");
    setReason("");
    setGrantOpen(true);
    api.getAdminYaongconBrands().then((res) => {
      if (res?.result === "ok") {
        setBrands(res.infos ?? []);
      }
    });
    api.getAdminYaongconGoods("전체").then((res) => {
      if (res?.result === "ok") {
        setGoods(res.infos ?? []);
      }
    });
  }

  function onBrandChange(nextBrand) {
    setBrandName(nextBrand);
    setGoodsId("");
    api.getAdminYaongconGoods(nextBrand).then((res) => {
      if (res?.result === "ok") {
        setGoods(res.infos ?? []);
      } else {
        setGoods([]);
      }
    });
  }

  function onSubmitGrant() {
    if (!goodsId) {
      setToastMsg("지급할 상품을 선택해주세요.");
      setShow(true);
      return;
    }
    setConfirmOpen(true);
  }

  function onConfirmGrant() {
    if (submitting) return;
    setSubmitting(true);
    api.adminGrantYaongcon({
      userNo: Number(userNo),
      goodsId,
      reason: reason.trim() || undefined,
    }).then((res) => {
      if (res?.result === "ok") {
        setToastMsg("야옹콘이 지급되었습니다.");
        setShow(true);
        setConfirmOpen(false);
        setGrantOpen(false);
        fetchCoupons();
      } else {
        setToastMsg(res?.message || "지급에 실패했습니다.");
        setShow(true);
        setConfirmOpen(false);
      }
    }).finally(() => setSubmitting(false));
  }

  return (
    <div>
      <div className="user-coupon-toolbar flex">
        <button type="button" className="blue-btn" onClick={openGrantModal}>
          야옹콘 지급
        </button>
      </div>

      <Table bordered className="basic-table user-history-table">
        <thead>
          <tr>
            <th>발급일</th>
            <th>쿠폰명</th>
            <th>유형</th>
            <th>상태</th>
            <th>시리얼</th>
            <th>사용일</th>
            <th>만료일</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? items.map((item) => (
            <tr key={item.couponId}>
              <td>{dateFormat(item.createdAt, true)}</td>
              <td>{item.itemName || item.goodsName || "-"}</td>
              <td>{item.type || "-"}</td>
              <td>{COUPON_STATUS_LABEL[item.status] || item.status || "-"}</td>
              <td>{item.couponSerial || item.epin || "-"}</td>
              <td>{item.usedAt ? dateFormat(item.usedAt, true) : "-"}</td>
              <td>{item.expiredAt ? dateFormat(item.expiredAt, true) : "-"}</td>
            </tr>
          )) : (
            <tr>
              <td colSpan={7} style={{textAlign: "center"}}>쿠폰 내역이 없습니다.</td>
            </tr>
          )}
        </tbody>
      </Table>

      <ModalDialog
        show={grantOpen}
        title="야옹콘 무상 지급"
        onHide={() => setGrantOpen(false)}
        btnText="지급하기"
        hasTwoButtons
        secondBtnText="취소"
        onOk={onSubmitGrant}
        onClickSecond={() => setGrantOpen(false)}
      >
        <div className="user-grant-form">
          <p className="user-grant-note">사용자 포인트는 차감되지 않습니다. Gift-n EPIN이 즉시 발급됩니다.</p>
          <label>브랜드</label>
          <Form.Select value={brandName} onChange={(e) => onBrandChange(e.target.value)}>
            <option value="전체">전체</option>
            {brands.map((brand) => (
              <option key={brand} value={brand}>{brand}</option>
            ))}
          </Form.Select>
          <label>상품</label>
          <Form.Select value={goodsId} onChange={(e) => setGoodsId(e.target.value)}>
            <option value="">상품을 선택하세요</option>
            {goods.map((item) => (
              <option key={item.goodsId} value={item.goodsId}>
                [{item.brandName}] {item.goodsName} ({(item.price ?? 0).toLocaleString()}P)
              </option>
            ))}
          </Form.Select>
          <label>지급 사유</label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="예: 이벤트 보상, CS 보상 등"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </ModalDialog>

      <ModalDialog
        show={confirmOpen}
        title="지급 확인"
        onHide={() => !submitting && setConfirmOpen(false)}
        btnText={submitting ? "지급 중..." : "확인"}
        hasTwoButtons
        secondBtnText="취소"
        onOk={onConfirmGrant}
        onClickSecond={() => !submitting && setConfirmOpen(false)}
      >
        <p style={{textAlign: "center"}}>
          {selectedGoods
            ? `[${selectedGoods.brandName}] ${selectedGoods.goodsName} 을(를) 무상 지급하시겠습니까?`
            : "선택한 상품을 무상 지급하시겠습니까?"}
        </p>
      </ModalDialog>
    </div>
  );
}

export default function UserDetail() {
  const api = new AdminApi();
  const navigate = useNavigate();
  const {userNo} = useParams();
  const [user, setUser] = useState(null);

  const fetchUser = useCallback(() => {
    api.getAdminUser(userNo).then((res) => {
      if (res?.result === "ok") {
        setUser(res.user);
      }
    });
  }, [userNo]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return (
    <div className="user-admin">
      <div className="tab-depth-line">
        <h1 onClick={() => navigate("/user")} style={{cursor: "pointer"}}>사용자 관리</h1>
        <h1>&gt; {user?.nickname || `회원 #${userNo}`}</h1>
      </div>

      <UserInfoBar user={user}/>

      <Tabs
        className="user-detail-tabs"
        tabNames={["포인트 내역", "간식 내역", "쿠폰 내역"]}
        tabPages={[
          <PointHistoryTab key="point" userNo={Number(userNo)} onGranted={fetchUser}/>,
          <SnackHistoryTab key="snack" userNo={Number(userNo)} onGranted={fetchUser}/>,
          <CouponHistoryTab key="coupon" userNo={Number(userNo)}/>,
        ]}
      />
    </div>
  );
}
